import { tx } from "../lib/i18n";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { SlidersHorizontal, X } from "lucide-react";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useMeta } from "../lib/useMeta";
import { weekdayNames } from "../lib/format";
import { LessonCard } from "../components/site/LessonCard";
import { PageHero, Spinner } from "../components/site/Layout";

const KEYS = ["island", "school_type", "grade", "subject", "language", "format", "weekday", "time", "max_price"];

const Sel = ({ label, value, onChange, options, testid }) => {
  const { t } = useI18n();
  return (
    <label className="block">
      <span className="osk-label">{label}</span>
      <select className="osk-input" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}>
        <option value="">{t("filter.any")}</option>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  );
};

export default function Catalog() {
  const { t, lang } = useI18n();
  const meta = useMeta();
  const [params, setParams] = useSearchParams();
  const [lessons, setLessons] = useState(null);
  const [open, setOpen] = useState(false);
  const f = Object.fromEntries(KEYS.map((k) => [k, params.get(k) || ""]));

  useEffect(() => {
    setLessons(null);
    const q = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
    api.get("/lessons", { params: q }).then((r) => setLessons(r.data)).catch(() => setLessons([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.toString()]);

  const set = (k, v) => {
    const p = new URLSearchParams(params);
    v ? p.set(k, v) : p.delete(k);
    if (k === "school_type") p.delete("grade");
    setParams(p);
  };
  const opts = (arr) => (arr || []).map((x) => ({ value: x, label: x }));
  const grades = f.school_type ? meta?.grades?.[f.school_type] : [...new Set(Object.values(meta?.grades || {}).flat())];

  const filters = (
    <div className="space-y-4" data-testid="catalog-filters">
      <Sel label={t("search.island")} value={f.island} onChange={(v) => set("island", v)} testid="filter-island"
        options={(meta?.islands || []).filter((i) => i.enabled).map((i) => ({ value: i.code, label: i.name }))} />
      <Sel label={t("search.school")} value={f.school_type} onChange={(v) => set("school_type", v)} options={opts(meta?.school_types)} testid="filter-school-type" />
      <Sel label={t("filter.grade")} value={f.grade} onChange={(v) => set("grade", v)} options={opts(grades)} testid="filter-grade" />
      <Sel label={t("search.subject")} value={f.subject} onChange={(v) => set("subject", v)} options={opts(meta?.subjects)} testid="filter-subject" />
      <Sel label={t("filter.language")} value={f.language} onChange={(v) => set("language", v)} options={opts(meta?.languages)} testid="filter-language" />
      <Sel label={t("filter.format")} value={f.format} onChange={(v) => set("format", v)} options={(meta?.formats || []).map((x) => ({ value: x, label: tx(x) }))} testid="filter-format" />
      <Sel label={t("filter.day")} value={f.weekday} onChange={(v) => set("weekday", v)} testid="filter-weekday"
        options={weekdayNames(lang).map((d, i) => ({ value: String(i), label: d }))} />
      <Sel label={t("filter.time")} value={f.time} onChange={(v) => set("time", v)} testid="filter-time"
        options={["ochtend", "middag", "avond"].map((x) => ({ value: x, label: t(`time.${x}`) }))} />
      <label className="block">
        <span className="osk-label">{t("filter.price")}</span>
        <input type="number" min="0" className="osk-input" value={f.max_price} placeholder="AWG" onChange={(e) => set("max_price", e.target.value)} data-testid="filter-max-price" />
      </label>
      <button className="text-sm font-bold text-osk-blue hover:underline" onClick={() => setParams({})} data-testid="filter-reset-btn">{t("catalog.reset")}</button>
    </div>
  );

  return (
    <div data-testid="catalog-page">
      <PageHero title={t("catalog.title")} sub={t("catalog.sub")} />
      <div className="osk-container grid gap-8 pb-10 md:grid-cols-[260px_1fr]">
        <aside className="hidden md:block"><div className="osk-card sticky top-24 p-5">{filters}</div></aside>
        <div>
          <div className="mb-5 flex items-center justify-between">
            <p className="text-sm font-semibold text-osk-text" data-testid="catalog-result-count">{lessons ? t("catalog.results", { n: lessons.length }) : ""}</p>
            <button className="osk-btn-outline py-2 md:hidden" onClick={() => setOpen(true)} data-testid="catalog-open-filters-btn"><SlidersHorizontal className="h-4 w-4" />{t("catalog.filters")}</button>
          </div>
          {lessons === null ? <Spinner /> : lessons.length === 0 ? (
            <div className="osk-card p-10 text-center text-osk-text" data-testid="catalog-empty">{t("catalog.none")}</div>
          ) : (
            <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3" data-testid="catalog-results">
              {lessons.map((l) => <LessonCard key={l.id} lesson={l} />)}
            </div>
          )}
        </div>
      </div>
      {open && (
        <div className="fixed inset-0 z-50 bg-black/30 md:hidden" onClick={() => setOpen(false)}>
          <div className="absolute inset-x-0 bottom-0 max-h-[85vh] overflow-y-auto rounded-t-3xl bg-white p-6" onClick={(e) => e.stopPropagation()}>
            <div className="mb-4 flex items-center justify-between"><h3 className="text-lg">{t("catalog.filters")}</h3><button onClick={() => setOpen(false)} data-testid="catalog-close-filters-btn"><X /></button></div>
            {filters}
          </div>
        </div>
      )}
    </div>
  );
}
